import { valibotResolver } from '@hookform/resolvers/valibot'
import { useForm } from 'react-hook-form'

import { EXAMPLES } from '@/constants/common/message.constants'
import { type CreateCustomerDto, type CustomerEntity } from '@/libs/api'
import {
  BANK_ACCOUNT_TYPE,
  type BankAccountType,
  type RecordingDateType,
  ROUND_TYPE,
  type RoundType,
  TAX_CALCULATION_TYPE,
  type TaxCalculationType,
  type SightMonthOffset,
  type DueDateType,
  SIGHT_MONTH_OFFSET,
  DUE_DATE_TYPE
} from '@/libs/api/types'

import { CreateCustomerDtoSchema } from '../../-models'

export type CustomerFormValue = {
  dcId: number | undefined // 拠点ID
  code: string
  name: string
  nameKana: string | null
  phoneNumber: string | null
  faxNumber: string | null
  email: string | null
  departmentName: string | null
  postalCode: string | null
  prefecture: string | null
  city: string | null
  blockNumber: string | null
  buildingName: string | null
  bankName: string | null
  bankCode: string | null
  bankBranchCode: string | null
  bankBranchName: string | null
  bankAccountType: BankAccountType
  bankAccountNumber: string | null
  bankAccountName: string | null
  sightMonthOffset: SightMonthOffset // サイト
  dueDateType: DueDateType // 期日タイプ
  dueDay: number | null // 期日
  moneyType: string // 金種
  companyName: string | null
  invoiceRegistrationNumber: string | null
  enable: boolean
  taxCalculationType: TaxCalculationType
  roundType: RoundType
  isSubcontractFeeBillable: boolean
  siteGroupCode?: string | null
  defaultTaxCategoryId: number | null // デフォルト税区分ID（VOC_PJ-462）
  defaultIsTaxInclusive: boolean | null // 内税既定（VOC_PJ-844 #3624 請求側 基本運賃カスケード: 取引先。null=未設定/継承）
  // 荷役作業の既定値（#4741）。配送依頼の新規作成時の初期表示にのみ使う。null=未設定
  defaultIsLoadingWorkOutsourced: boolean | null
  defaultLoadingWorkMinutes: number | null
  defaultIsUnloadingWorkOutsourced: boolean | null
  defaultUnloadingWorkMinutes: number | null
  defaultRoundType: RoundType | null // デフォルト端数処理（#4274 税メタカスケード: 取引先層。null=拠点設定に従う）
  billingShipperId: number | null // 請求先荷主ID（VOC_PJ-566,900 #4037 自己参照。null=自分自身が請求先）
  incomeRecordingDateType: RecordingDateType | null // 請求計上基準（null=拠点設定に従う、VOC_PJ-55 #3516）
  /**
   * VOC_PJ-29,431 #1857: デフォルト付帯費用カテゴリ ID 配列。
   * 新規配送依頼作成時に初期表示される項目。Customer のみ実装 (Partner は対象外、shimizu-pg 指摘 2)。
   * 保存は別 endpoint (`PUT /customers/:id/default-additional-payment-categories`)。
   */
  defaultAdditionalPaymentCategoryIds: number[]
}

export const initialCustomerFormValue: CustomerFormValue = {
  dcId: undefined,
  name: '',
  code: '',
  nameKana: null,
  phoneNumber: null,
  faxNumber: null,
  email: null,
  departmentName: null,
  postalCode: null,
  prefecture: null,
  city: null,
  blockNumber: null,
  buildingName: null,
  bankName: null,
  bankCode: null,
  bankBranchCode: null,
  bankBranchName: null,
  bankAccountType: BANK_ACCOUNT_TYPE.SAVINGS,
  bankAccountNumber: null,
  bankAccountName: null,
  moneyType: EXAMPLES.MONEY_TYPE,
  sightMonthOffset: SIGHT_MONTH_OFFSET.MONTH_1,
  dueDateType: DUE_DATE_TYPE.END_OF_MONTH,
  dueDay: null,
  companyName: null,
  invoiceRegistrationNumber: null,
  enable: true,
  taxCalculationType: TAX_CALCULATION_TYPE.BULK_CALCULATION,
  // #3992: 新規登録時の既定を切り捨てへ変更 (BE スキーマの @default(ROUND_DOWN) と合わせる)
  roundType: ROUND_TYPE.ROUND_DOWN,
  isSubcontractFeeBillable: false,
  defaultTaxCategoryId: null,
  defaultIsTaxInclusive: null,
  defaultIsLoadingWorkOutsourced: null,
  defaultLoadingWorkMinutes: null,
  defaultIsUnloadingWorkOutsourced: null,
  defaultUnloadingWorkMinutes: null,
  defaultRoundType: null,
  billingShipperId: null,
  incomeRecordingDateType: null,
  defaultAdditionalPaymentCategoryIds: []
}

export function mapToCustomerFormValue(
  customer: CustomerEntity
): CustomerFormValue {
  return {
    dcId: customer.dcId,
    name: customer.name,
    code: customer.code,
    nameKana: customer.nameKana || null,
    phoneNumber: customer.phoneNumber || null,
    faxNumber: customer.faxNumber || null,
    email: customer.email || null,
    departmentName: customer.departmentName || null,
    postalCode: customer.postalCode || null,
    prefecture: customer.prefecture || null,
    city: customer.city || null,
    blockNumber: customer.blockNumber || null,
    buildingName: customer.buildingName || null,
    bankName: customer.bankName || null,
    bankCode: customer.bankCode || null,
    bankBranchCode: customer.bankBranchCode || null,
    bankBranchName: customer.bankBranchName || null,
    bankAccountType: customer.bankAccountType || BANK_ACCOUNT_TYPE.SAVINGS,
    bankAccountNumber: customer.bankAccountNumber || null,
    bankAccountName: customer.bankAccountName || null,
    moneyType: customer.moneyType,
    sightMonthOffset: customer.sightMonthOffset,
    dueDateType: customer.dueDateType,
    dueDay: customer.dueDay || null,
    companyName: customer.companyName || null,
    invoiceRegistrationNumber: customer.invoiceRegistrationNumber || null,
    enable: customer.enable,
    taxCalculationType: customer.taxCalculationType,
    roundType: customer.roundType,
    isSubcontractFeeBillable: customer.isSubcontractFeeBillable,
    siteGroupCode: customer.siteGroupCode ?? null,
    defaultTaxCategoryId: customer.defaultTaxCategoryId ?? null,
    defaultIsTaxInclusive: customer.defaultIsTaxInclusive ?? null,
    defaultIsLoadingWorkOutsourced:
      customer.defaultIsLoadingWorkOutsourced ?? null,
    defaultLoadingWorkMinutes: customer.defaultLoadingWorkMinutes ?? null,
    defaultIsUnloadingWorkOutsourced:
      customer.defaultIsUnloadingWorkOutsourced ?? null,
    defaultUnloadingWorkMinutes: customer.defaultUnloadingWorkMinutes ?? null,
    defaultRoundType: customer.defaultRoundType ?? null,
    billingShipperId: customer.billingShipperId ?? null,
    incomeRecordingDateType: customer.incomeRecordingDateType ?? null,
    // VOC_PJ-29,431 #1857: BE は CustomerExEntity.defaultAdditionalPaymentCategories (Entity[]) を返すので id 配列に変換。
    // CustomerEntity (リスト用、Ex なし) には含まれないため optional cast。
    defaultAdditionalPaymentCategoryIds:
      (
        customer as { defaultAdditionalPaymentCategories?: { id: number }[] }
      ).defaultAdditionalPaymentCategories?.map((c) => c.id) ?? []
  }
}

export const useCustomerDetailPageForm = (user: CustomerEntity) =>
  useForm<CreateCustomerDto>({
    values: user,
    resolver: valibotResolver(CreateCustomerDtoSchema)
  })
